'use client';

import { Check, Globe } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTransition } from 'react';
import { useI18n } from '@/components/i18n-provider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LOCALES, TRANSLATIONS } from '@/i18n/config';
import { setLocale } from '@/lib/locale-actions';
import { cn } from '@/lib/utils';

type TProps = {
  /** `dark` sits on the blue sidebar; `light` on a white page such as the sign-in screen. */
  tone?: 'dark' | 'light';
  /** Only the globe — for the collapsed sidebar rail. */
  compact?: boolean;
  className?: string;
};

export function LanguageSwitcher({ tone = 'light', compact = false, className }: TProps) {
  const { locale, t } = useI18n();
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const current = LOCALES.find(({ id }) => id === locale) ?? LOCALES[0];

  const choose = (id: string) =>
    startTransition(async () => {
      await setLocale(id);
      router.refresh();
    });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          aria-label={t(TRANSLATIONS.navigation.language)}
          disabled={pending}
          className={cn(
            'gap-2',
            tone === 'dark'
              ? 'text-sidebar-foreground hover:bg-white/10 hover:text-white'
              : 'text-muted-foreground hover:text-foreground',
            className,
          )}
        >
          <Globe className={cn(pending && 'animate-spin')} />
          {!compact && <span>{current.name}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side={tone === 'dark' ? 'top' : 'bottom'} className="min-w-44">
        <DropdownMenuLabel>{t(TRANSLATIONS.navigation.language)}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LOCALES.map(({ id, name }) => (
          <DropdownMenuItem key={id} onSelect={() => choose(id)} lang={id} className="justify-between">
            {name}
            {id === locale && <Check className="text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
